import { useState, useEffect, useContext, useRef, RefObject } from "react";
import { MapContext } from "../../providers/MapProvider";
import { GoogleMap } from "../../types/map";
import { useAppSelector } from "../../redux/hooks";
import useDeepCompareEffectForMaps from "../../hooks/useDeepCompareEffectForMaps";
import { selectReferenceItinerary } from "../../redux/slices/shed/shedSlice";

type DirectionRenderer = google.maps.DirectionsRenderer;
type GoogleMapOptions = google.maps.MapOptions;

type Props = {
  mapWrapperReference: RefObject<HTMLDivElement | null>;
  onMapInitialized: (googleMap: GoogleMap) => void;
  googleMapOptions: GoogleMapOptions;
  suppressDirectionMarkers?: boolean;
};

const useMapRenderer = ({
  onMapInitialized,
  mapWrapperReference,
  googleMapOptions,
  suppressDirectionMarkers = false,
}: Props) => {
  const { initialized: mapInitialized, mapsLibraries } = useContext(MapContext);
  const googleMapRef = useRef<GoogleMap | null>(null);
  const extraRenderersRef = useRef<DirectionRenderer[]>([]);
  const flightPolylinesRef = useRef<google.maps.Polyline[]>([]);
  const [directionsRenderer, setDirectionRenderer] =
    useState<DirectionRenderer | null>(null);
  const referenceItinerary = useAppSelector(selectReferenceItinerary);

  const clearExtraOverlays = () => {
    extraRenderersRef.current.forEach((renderer) => renderer.setMap(null));
    extraRenderersRef.current = [];
    flightPolylinesRef.current.forEach((polyline) => polyline.setMap(null));
    flightPolylinesRef.current = [];
  };

  // Google map binding initializer
  useEffect(() => {
    if (!(mapWrapperReference?.current && mapInitialized && mapsLibraries)) {
      return;
    }

    const mapOptions = {
      gestureHandling: "greedy" as google.maps.GestureHandlingOptions,
      mapId: import.meta.env.REACT_APP_GOOGLE_MAP_ID,
      mapTypeControl: true,
      mapTypeControlOptions: {
        position: google.maps.ControlPosition.TOP_LEFT, // Position of the control
        mapTypeIds: [
          google.maps.MapTypeId.ROADMAP,
          google.maps.MapTypeId.SATELLITE,
        ],
      },
    };
    const googleMap = new mapsLibraries.Map(
      mapWrapperReference.current,
      mapOptions
    );
    const mapDirectionsRenderer = new google.maps.DirectionsRenderer({
      suppressMarkers: suppressDirectionMarkers,
      // Viewport is controlled via itinerary.bounds (includes flights).
      preserveViewport: true,
    });

    mapDirectionsRenderer.setMap(googleMap);
    setDirectionRenderer(mapDirectionsRenderer);

    googleMapRef.current = googleMap;
    console.log("[useMapRenderer] Google Map Initialized");
    onMapInitialized(googleMap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapWrapperReference, mapsLibraries, mapInitialized, suppressDirectionMarkers]);

  useEffect(() => {
    directionsRenderer?.setOptions({
      suppressMarkers: suppressDirectionMarkers,
      preserveViewport: true,
    });
  }, [directionsRenderer, suppressDirectionMarkers]);

  // Google map binding generic synchronizer
  useDeepCompareEffectForMaps(() => {
    mapInitialized &&
      googleMapRef &&
      googleMapRef.current &&
      googleMapRef.current.setOptions(googleMapOptions);
  }, [googleMapOptions, mapInitialized]);

  // Google Map binding direction synchronizer
  useEffect(() => {
    const map = googleMapRef.current;

    if (!map) {
      return;
    }

    if (!referenceItinerary) {
      clearExtraOverlays();
      return;
    }

    if (directionsRenderer && referenceItinerary.googleDirection) {
      directionsRenderer.setMap(map);
      directionsRenderer.setDirections(referenceItinerary.googleDirection);
    } else if (directionsRenderer) {
      directionsRenderer.setMap(null);
    }

    const extraDirections = referenceItinerary.googleDirections ?? [];
    extraRenderersRef.current.forEach((renderer) => renderer.setMap(null));
    extraRenderersRef.current = extraDirections.map((result) => {
      const renderer = new google.maps.DirectionsRenderer({
        suppressMarkers: true,
        preserveViewport: true,
      });
      renderer.setMap(map);
      renderer.setDirections(result);
      return renderer;
    });

    flightPolylinesRef.current.forEach((polyline) => polyline.setMap(null));
    flightPolylinesRef.current = (referenceItinerary.flightHops ?? []).map(
      (hop) => {
        const polyline = new google.maps.Polyline({
          path: [hop.from, hop.to],
          geodesic: true,
          strokeColor: "#1565c0",
          strokeOpacity: 0,
          strokeWeight: 4,
          icons: [
            {
              icon: {
                path: "M 0,-1 0,1",
                strokeOpacity: 1,
                strokeWeight: 4,
                scale: 3,
              },
              offset: "0",
              repeat: "14px",
            },
          ],
        });
        polyline.setMap(map);
        return polyline;
      }
    );

    // Fit after overlays so DirectionsRenderer cannot override flight-inclusive bounds.
    if (referenceItinerary.bounds) {
      map.fitBounds(referenceItinerary.bounds);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleMapRef, referenceItinerary, directionsRenderer]);
};

export default useMapRenderer;
